import React from 'react';
import { format } from 'date-fns';
import { range } from 'lodash-es';

import {
  Box,
  DateInput,
  DatePicker,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import type { Section } from '../../../models/index';
import { ArsenicContaminationSection, Field } from '../../../models/index';
import { ARSENIC_CONTAMINATION_ZONE } from '../../../constants/index';
import { DATE_PICKER_FORMAT } from '../../../constants/stringConstants';
import YesAndNo from '../../Widgets/YesAndNo';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_REPORTS = 3;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: ArsenicContaminationSection) => void;
};

export default function ArsenicContaminationZone({ section, setSectionValues, setSection }: Props): JSX.Element {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: ArsenicContaminationSection,
  });

  function onRemoveAll(): void {
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function setName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.REPORT_NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setNumber(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.REPORT_NUMBER, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setDate(index: number, formattedDate: string): void {
    const fieldName = Field.getIndexedFieldName(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.REPORT_DATE, Number(index));

    setSectionValues({ [fieldName]: formattedDate });
  }

  function onYesArsenicContaminationZone(): void {
    setSectionValues({
      [ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onNoArsenicContaminationZone(): void {
    setSectionValues({
      [ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.IS_ENABLED]: false,
    });
    onRemoveAll();
  }

  function onYesHasBeenTested(): void {
    setSectionValues({
      [ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.HAS_BEEN_TESTED]: true,
    });
    onAddOne();
  }

  function onNoHasBeenTested(): void {
    setSectionValues({
      [ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.HAS_BEEN_TESTED]: false,
    });
    onRemoveAll();
  }

  function renderReport(index: number): React.ReactNode {
    const nameField = section.getField(
      Field.getIndexedFieldName(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.REPORT_NAME, index),
    );
    const numberField = section.getField(
      Field.getIndexedFieldName(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.REPORT_NUMBER, index),
    );
    const dateField = section.getField(
      Field.getIndexedFieldName(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.REPORT_DATE, index),
    );

    return (
      <>
        <Box css={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'layout.tighter' }}>
          <Heading level={4} textStyle="responsive.heading-xs" id={`arsenic-contamination-report-#${index + 1}`}>
            Report #{index + 1}
          </Heading>
          {index > 0 && (
            <TextButton data-index={index} data-test="delete-arsenic-contamination-report" onClick={onDeleteOne}>
              Delete
            </TextButton>
          )}
        </Box>
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={<Label>Name</Label>}
          control={<Input data-index={index} value={nameField?.enteredValue} onChange={setName} />}
        />
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={<Label>Number</Label>}
          control={<Input data-index={index} value={numberField?.enteredValue} onChange={setNumber} />}
        />
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={<Label>Date</Label>}
          control={
            <DatePicker
              dateFormat={DATE_PICKER_FORMAT}
              value={dateField?.enteredValue}
              onValueChange={(value) => {
                const formatted = typeof value === 'string' ? value : format(value, DATE_PICKER_FORMAT);
                setDate(index, formatted);
              }}
              maxDate={new Date()}
              closeModalOnDateSelect
              input={<DateInput />}
            />
          }
        />
      </>
    );
  }

  const totalArsenicContaminationReports = section.getTotalIndexes();
  if (totalArsenicContaminationReports == null) {
    return null;
  }

  const arsenicContaminationEnabled = section.getFieldValue(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.IS_ENABLED);
  const hasBeenTestedEnabled = section.getFieldValue(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.HAS_BEEN_TESTED);

  return (
    <>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>Is the rental property within the South Minneapolis Arsenic Contamination Zone?</Label>}
        control={
          <YesAndNo
            yesHandler={onYesArsenicContaminationZone}
            noHandler={onNoArsenicContaminationZone}
            yesActive={arsenicContaminationEnabled}
          />
        }
      />
      {arsenicContaminationEnabled && (
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          label={<Label>Has the US EPA tested for arsenic at this property?</Label>}
          control={
            <YesAndNo
              yesHandler={onYesHasBeenTested}
              noHandler={onNoHasBeenTested}
              yesActive={section.getFieldValue(ARSENIC_CONTAMINATION_ZONE.FIELD_NAME.HAS_BEEN_TESTED)}
            />
          }
          description={
            <FormHelp>
              You must provide copies of any reports before the lease is signed by tenant. Later on, you will have the
              ability to attach any reports to your lease.
            </FormHelp>
          }
        />
      )}
      {hasBeenTestedEnabled && (
        <>
          {range(0, totalArsenicContaminationReports).map((index: number) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />}
              {renderReport(index)}
            </React.Fragment>
          ))}
          {totalArsenicContaminationReports < MAXIMUM_REPORTS ? (
            totalArsenicContaminationReports >= 0 && (
              <TextButton onClick={onAddOne} icon={<IconPlusFilled />} data-test="add-another">
                Add another report
              </TextButton>
            )
          ) : (
            <Text data-test="maximum-reached">
              You’ve added {MAXIMUM_REPORTS} reports, which is the maximum allowed.
            </Text>
          )}
        </>
      )}
    </>
  );
}
